import { useCallback, useEffect, useState } from "react";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle,
  ChevronRight,
  Loader2,
  Radar,
} from "lucide-react";
import Select from "../common/Select";
import TeamOperatorsModal, { OperatorAvatar } from "./TeamOperatorsModal";
import TeamNoiseModal from "./TeamNoiseModal";
import { runHasEntries } from "./EntryInjectGroups";
import { useToast } from "../ToastContext";
import { getTeamMembers } from "../../api/services/companyAdminService";
import { injectTeamScenarioStage } from "../../api/services/platformAdminService";
import { applyAdvancedResults, describeInjectOutcome } from "./injectOutcome";

// Loading / error / empty placeholder; returns null when there is content.
export function RunsStatus({
  isLoading,
  loadError,
  isEmpty,
  emptyTitle,
  emptyText,
  loadingFallback,
}) {
  if (isLoading && loadingFallback) return loadingFallback;
  if (isLoading)
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="h-6 w-6 animate-spin text-(--color-primary)" />
      </div>
    );
  if (!loadError && !isEmpty) return null;

  const Icon = loadError ? AlertCircle : Radar;
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-2 text-center">
      <Icon
        className={`h-6 w-6 ${loadError ? "text-red-500" : "text-(--color-text-muted)"}`}
      />
      <p className="text-base font-semibold text-(--color-text-primary)">
        {loadError ? "Failed to load" : emptyTitle}
      </p>
      <p className="text-sm text-(--color-text-muted) max-w-xs">
        {loadError || emptyText}
      </p>
    </div>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function getDifficultyText(difficulty) {
  const n = (difficulty || "").trim().toLowerCase();
  if (n === "easy" || n === "low") return "text-emerald-400";
  if (n === "medium" || n === "intermediate") return "text-amber-400";
  if (n === "hard" || n === "advanced" || n === "high") return "text-rose-400";
  return "text-(--color-text-muted)";
}

function formatTimeLeft(expiresAt) {
  const minutes = Math.max(
    0,
    Math.round((new Date(expiresAt) - Date.now()) / 60000),
  );
  const hours = Math.floor(minutes / 60);
  return hours > 0 ? `${hours}h ${minutes % 60}m` : `${minutes}m`;
}

// ─── small building blocks ────────────────────────────────────────────────────

function ReadyBadge({ ready }) {
  if (ready)
    return (
      <span className="shrink-0 text-[10px] font-semibold px-1.5 py-px rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-400 inline-flex items-center gap-1">
        <CheckCircle className="h-2.5 w-2.5" />
        Ready
      </span>
    );
  return (
    <span className="shrink-0 text-[10px] font-semibold px-1.5 py-px rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-400 inline-flex items-center gap-1">
      <Loader2 className="h-2.5 w-2.5 animate-spin" />
      Provisioning
    </span>
  );
}

function formatStageLabel({ stage, name }) {
  return name ? `Stage ${stage} · ${name}` : `Stage ${stage}`;
}

// One segment per stage: completed stages are filled, the current one glows.
function SegmentedStageBar({ stages, currentStage }) {
  return (
    <div className="flex gap-1">
      {stages.map((s) => {
        const segmentClass =
          s.stage === currentStage
            ? "bg-linear-to-r from-orange-500 to-orange-300"
            : s.stage < currentStage
              ? "bg-orange-600/80"
              : "bg-gray-200 dark:bg-white/6";
        return (
          <span
            key={s.stage}
            title={formatStageLabel(s)}
            className={`h-1.5 flex-1 rounded-full ${segmentClass}`}
          />
        );
      })}
    </div>
  );
}

// ─── ScenarioGroupCard (overview) ─────────────────────────────────────────────

// Leader = furthest stage reached; average = mean current stage, rounded to a
// whole stage number.
function summarizeStages(runs) {
  const totalCurrent = runs.reduce((sum, r) => sum + (r.current_stage || 0), 0);
  return {
    leaderStage: Math.max(0, ...runs.map((r) => r.current_stage || 0)),
    totalStages: Math.max(0, ...runs.map((r) => r.total_stages || 0)),
    averageStage: runs.length ? Math.round(totalCurrent / runs.length) : 0,
  };
}

function getStageColumnClass(stage, count, leaderStage) {
  if (stage === leaderStage && count > 0)
    return "bg-linear-to-t from-emerald-500 to-emerald-300 shadow-[0_0_10px_rgba(52,211,153,0.6)] animate-pulse";
  if (count > 0) return "bg-linear-to-t from-emerald-600/80 to-emerald-400/80";
  if (stage <= leaderStage) return "bg-emerald-500/15";
  return "bg-white/6";
}

// One column per stage; height reflects how many teams sit on that stage and
// the leader's column pulses so the card reads as live activity.
function StageHistogram({ runs, totalStages, leaderStage }) {
  const counts = runs.reduce((acc, r) => {
    acc[r.current_stage] = (acc[r.current_stage] || 0) + 1;
    return acc;
  }, {});
  const maxCount = Math.max(1, ...Object.values(counts));

  return (
    <div className="flex h-14 items-end gap-0.75">
      {Array.from({ length: totalStages }, (_, i) => {
        const stage = i + 1;
        const count = counts[stage] || 0;
        const height = count ? 30 + (70 * count) / maxCount : 12;
        return (
          <span
            key={stage}
            title={`Stage ${stage}: ${count} ${count === 1 ? "team" : "teams"}`}
            style={{ height: `${height}%` }}
            className={`w-1.5 rounded-sm transition-all duration-500 ${getStageColumnClass(stage, count, leaderStage)}`}
          />
        );
      })}
    </div>
  );
}

export function ScenarioGroupCard({ group, onOpen }) {
  const { scenario, runs } = group;
  const teamCount = runs.length;
  const { leaderStage, totalStages, averageStage } = summarizeStages(runs);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className="group relative w-full text-left rounded-xl border border-(--color-border) qv-card qv-noise flex items-center gap-5 p-4 cursor-pointer focus:outline-none focus:ring-2 focus:ring-(--color-primary)/40"
    >
      <div className="shrink-0 rounded-lg border border-(--color-border) bg-gray-50/80 dark:bg-black/20 px-3 py-2.5">
        <div className="flex items-baseline gap-1">
          <span className="text-xl font-semibold leading-none text-(--color-text-primary) tabular-nums">
            {teamCount}
          </span>
          <span className="text-[10px] text-(--color-text-muted)">
            {teamCount === 1 ? "team" : "teams"}
          </span>
        </div>
        <div className="mt-2">
          <StageHistogram
            runs={runs}
            totalStages={totalStages}
            leaderStage={leaderStage}
          />
        </div>
        <div className="mt-1 flex justify-between font-mono text-[9px] text-(--color-text-muted)">
          <span>S1</span>
          <span>S{totalStages}</span>
        </div>
      </div>

      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex items-center gap-2.5 text-[11px] font-medium">
          <span className="inline-flex items-center gap-1.5 text-emerald-400">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
            </span>
            Live
          </span>
          {scenario?.difficulty && (
            <span className={getDifficultyText(scenario.difficulty)}>
              {scenario.difficulty}
            </span>
          )}
        </div>
        <h3 className="text-sm font-semibold text-(--color-text-primary) leading-snug line-clamp-2">
          {scenario?.title || "Untitled Scenario"}
        </h3>
        <div className="space-y-0.5 text-xs text-(--color-text-muted)">
          <p>
            Leader on stage{" "}
            <span className="font-semibold text-(--color-text-primary) tabular-nums">
              {leaderStage}
            </span>{" "}
            of{" "}
            <span className="font-semibold text-(--color-text-primary) tabular-nums">
              {totalStages}
            </span>
          </p>
          <p>
            Average stage across teams{" "}
            <span className="font-semibold text-(--color-text-primary) tabular-nums">
              {averageStage}
            </span>
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 pt-0.5 text-xs font-medium text-(--color-text-secondary) group-hover:text-(--color-primary) transition-colors">
          View teams
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </div>
  );
}

// Fixed bar heights (%) give the placeholder histogram a natural, uneven shape.
const SKELETON_HISTOGRAM = [12, 12, 45, 12, 70, 12, 100, 12, 12, 30, 12];

// Mirrors ScenarioGroupCard's layout so the grid doesn't jump when data lands.
export function ScenarioGroupCardSkeleton({ className = "flex" }) {
  return (
    <div
      aria-hidden="true"
      className={`w-full rounded-xl border border-(--color-border) qv-card qv-noise items-center gap-5 p-4 ${className}`}
    >
      <div className="shrink-0 rounded-lg border border-(--color-border) bg-gray-50/80 dark:bg-black/20 px-3 py-2.5">
        <div className="flex items-baseline gap-1">
          <span className="qv-skeleton h-5 w-5 rounded" />
          <span className="qv-skeleton h-2.5 w-7 rounded" />
        </div>
        <div className="mt-2 flex h-14 items-end gap-0.75">
          {SKELETON_HISTOGRAM.map((height, i) => (
            <span
              key={i}
              style={{ height: `${height}%` }}
              className="qv-skeleton w-1.5 rounded-sm"
            />
          ))}
        </div>
        <div className="mt-1.5 flex justify-between">
          <span className="qv-skeleton h-2 w-3 rounded" />
          <span className="qv-skeleton h-2 w-4 rounded" />
        </div>
      </div>

      <div className="min-w-0 flex-1 space-y-2.5">
        <div className="flex items-center gap-2.5">
          <span className="qv-skeleton h-3 w-10 rounded-full" />
          <span className="qv-skeleton h-3 w-14 rounded" />
        </div>
        <div className="space-y-1.5">
          <div className="qv-skeleton h-3.5 w-11/12 rounded" />
          <div className="qv-skeleton h-3.5 w-3/5 rounded" />
        </div>
        <div className="space-y-1.5">
          <div className="qv-skeleton h-3 w-2/5 rounded" />
          <div className="qv-skeleton h-3 w-1/2 rounded" />
        </div>
        <div className="qv-skeleton h-3 w-20 rounded" />
      </div>
    </div>
  );
}

// Mirrors TeamRunCard's layout so the detail page doesn't jump when data lands.
export function TeamRunCardSkeleton() {
  return (
    <div className="w-full rounded-lg border border-(--color-border) qv-card qv-noise p-4 space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1.5">
          <div className="qv-skeleton h-4 w-32 rounded" />
          <div className="qv-skeleton h-2.5 w-40 rounded" />
        </div>
        <div className="qv-skeleton h-6 w-14 rounded" />
      </div>
      <div className="space-y-1.5">
        <div className="qv-skeleton h-2.5 w-24 rounded" />
        <div className="qv-skeleton h-1.5 w-full rounded-full" />
      </div>
      <div className="border-t border-(--color-border) pt-3">
        <div className="qv-skeleton h-3 w-28 rounded" />
      </div>
      <div className="qv-skeleton h-11 w-full rounded-md" />
    </div>
  );
}

// ─── TeamRunCard (scenario detail) ────────────────────────────────────────────

export function TeamRunCard({ run, onStageAdvanced }) {
  const {
    team_name,
    org_name,
    started_by_username,
    expires_at,
    current_stage = 0,
    total_stages = 0,
    ready,
    stages = [],
  } = run;
  const currentStageInfo = stages.find((s) => s.stage === current_stage);
  const subtitle = [
    org_name,
    started_by_username && `started by ${started_by_username}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    // Raise the active card so the stage dropdown overlaps the card below it
    // instead of rendering underneath (which also stole hover and flickered).
    <div className="relative w-full rounded-lg border border-(--color-border) qv-card qv-noise p-4 space-y-3 hover:z-20 focus-within:z-20">
      {/* Identity */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="text-sm font-semibold text-(--color-text-primary) truncate">
              {team_name || "Unnamed Team"}
            </h3>
            <ReadyBadge ready={ready} />
          </div>
          <p className="mt-0.5 text-[11px] text-(--color-text-muted) truncate">
            {subtitle || "—"}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[9px] font-semibold uppercase tracking-[0.15em] text-(--color-text-muted)">
            Time left
          </p>
          <p className="text-xs font-semibold text-(--color-text-primary) tabular-nums">
            {formatTimeLeft(expires_at)}
          </p>
        </div>
      </div>

      {/* Stage progress */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between font-mono text-[11px] text-(--color-text-muted)">
          <span className="text-[9px] font-sans font-semibold uppercase tracking-[0.15em]">
            Attack chain
          </span>
          <span>
            Stage{" "}
            <span className="font-semibold text-orange-400">
              {current_stage}
            </span>
            /{total_stages}
            {currentStageInfo?.name ? ` · ${currentStageInfo.name}` : ""}
          </span>
        </div>
        <SegmentedStageBar stages={stages} currentStage={current_stage} />
      </div>

      <TeamOperatorsRow run={run} />

      <TeamInjectBar
        run={run}
        scenarioId={run.scenario_id}
        teamId={run.team_id}
        currentStage={current_stage}
        totalStages={total_stages}
        stages={stages}
        ready={ready}
        onStageAdvanced={onStageAdvanced}
      />
    </div>
  );
}

// The noise trigger that sits beside the stage Inject button; opens a modal to
// fire a single noise/decoy/department entry at this team.
function TeamNoiseButton({ run, ready }) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        title="Fire noise, decoy, or department traffic at this team"
        className="inline-flex h-8 items-center rounded-md border border-sky-500/40 bg-sky-500/10 px-4 text-xs font-semibold text-sky-400 transition-colors hover:bg-sky-500/20"
      >
        Noise
      </button>
      {isModalOpen && (
        <TeamNoiseModal
          run={run}
          ready={ready}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
}

const MAX_VISIBLE_AVATARS = 4;

function TeamOperatorsRow({ run }) {
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const closeModal = useCallback(() => setIsModalOpen(false), []);
  const hiddenCount = members.length - MAX_VISIBLE_AVATARS;

  useEffect(() => {
    let isActive = true;
    getTeamMembers(run.team_id)
      // Members are supplementary on the card; an empty list is a safe fallback.
      .then((data) => isActive && setMembers(data?.members || []))
      .catch(() => isActive && setMembers([]))
      .finally(() => isActive && setIsLoading(false));
    return () => {
      isActive = false;
    };
  }, [run.team_id]);

  return (
    <div className="flex items-center justify-between gap-4 border-t border-(--color-border) pt-3 text-[11px]">
      {isLoading ? (
        <span className="inline-flex items-center gap-2 text-(--color-text-muted)">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading operators
        </span>
      ) : (
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="group inline-flex items-center gap-2 text-(--color-text-muted) hover:text-(--color-text-primary) transition-colors"
        >
          {members.length > 0 && (
            <span className="flex -space-x-1.5">
              {members.slice(0, MAX_VISIBLE_AVATARS).map((m) => (
                <OperatorAvatar
                  key={m.id}
                  name={m.name || m.email}
                  className="h-6 w-6 text-[9px] ring-2 ring-(--color-bg)"
                />
              ))}
              {hiddenCount > 0 && (
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-(--color-bg-secondary) text-[9px] font-semibold text-(--color-text-secondary) ring-2 ring-(--color-bg)">
                  +{hiddenCount}
                </span>
              )}
            </span>
          )}
          {members.length} {members.length === 1 ? "operator" : "operators"}
          <ChevronRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
        </button>
      )}
      {isModalOpen && (
        <TeamOperatorsModal run={run} members={members} onClose={closeModal} />
      )}
    </div>
  );
}

// Only current+1 actually advances a run; other picks return
// blocked_prerequisite / skipped_ahead, which are surfaced via a toast.
function TeamInjectBar({
  run,
  scenarioId,
  teamId,
  currentStage,
  totalStages,
  stages,
  ready,
  onStageAdvanced,
}) {
  const { showToast } = useToast();
  const [isInjecting, setIsInjecting] = useState(false);
  const hasEntries = runHasEntries(run);

  const nextStage = currentStage + 1;
  const stageOptions = [];
  for (let s = nextStage; s <= totalStages; s += 1) {
    const name = stages.find((info) => info.stage === s)?.name;
    stageOptions.push({ value: s, label: name ? `${s}. ${name}` : `Stage ${s}` });
  }
  const [selectedStage, setSelectedStage] = useState(nextStage);
  // Keep the selection valid as the run advances (options are all >= nextStage).
  const effectiveStage = stageOptions.some((o) => o.value === selectedStage)
    ? selectedStage
    : nextStage;

  const allDone = currentStage >= totalStages;

  const handleInject = async () => {
    setIsInjecting(true);
    try {
      const data = await injectTeamScenarioStage({
        scenarioId,
        stage: effectiveStage,
        teamId,
      });
      const { type, message } = describeInjectOutcome(
        data?.results?.[0],
        effectiveStage,
      );
      showToast(message, type);
      applyAdvancedResults(data?.results, onStageAdvanced, effectiveStage);
    } catch (err) {
      showToast(err?.message || "Failed to send inject.", "error");
    } finally {
      setIsInjecting(false);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-md border border-(--color-border) bg-gray-50/80 dark:bg-black/20 px-3 py-2">
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-(--color-text-secondary)">
        <ArrowRight className="h-3 w-3 text-orange-400" />
        Team-wide inject
      </span>
      <div className="flex items-center gap-2">
        {allDone ? (
          <span className="text-[11px] text-(--color-text-muted)">
            All stages injected
          </span>
        ) : (
          <>
            <Select
              name="stage"
              value={effectiveStage}
              onChange={(e) => setSelectedStage(Number(e.target.value))}
              options={stageOptions}
              className="w-64"
              triggerClassName="h-8! px-3! border! rounded-md! [&>div]:min-w-0"
              valueClassName="text-xs! truncate"
              menuClassName="top-auto! bottom-full! mt-0! mb-1! max-h-40! rounded-md!"
              optionClassName="py-2! text-xs!"
            />
            <button
              type="button"
              onClick={handleInject}
              disabled={isInjecting || !ready}
              title={
                ready
                  ? "Fire this stage for the team"
                  : "AttackBox is not ready yet"
              }
              className="inline-flex items-center gap-1.5 h-8 px-4 rounded-md bg-orange-500 text-xs font-semibold text-white transition-colors hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isInjecting && <Loader2 className="h-3 w-3 animate-spin" />}
              {isInjecting ? "Injecting" : "Inject"}
            </button>
          </>
        )}
        {hasEntries && <TeamNoiseButton run={run} ready={ready} />}
      </div>
    </div>
  );
}

